import { useCurrentUser } from '../../../../contexts/CurrentUserContext';
import { projectRegions } from '../../../../hooks/use-viewer-regions';
import { usePermissions } from '../../../../hooks/usePermissions';
import { Permission } from '../../../../types/permissions';

import { flattenRegions, useRegionHierarchy } from './region-hierarchy';
import type { ScopeRegion } from './scope.types';

export interface OwnRegions {
  /**
   * Not yet known. Set until the viewer's permissions, the current user and,
   * for someone who needs it, the region list have all arrived. A page that
   * chooses its scope before then would fire national requests for a viewer who
   * is about to be scoped to their own regions.
   */
  pending: boolean;
  /**
   * The regions to default to, shallowest first: by depth, then by tree order
   * within a depth. A depth-first walk alone would put a child ahead of a later
   * top-level region. Empty for a viewer with the national sentinel, who opens
   * national, and for one with no assigned regions.
   */
  own: ScopeRegion[];
  /**
   * A viewer without read:all who has no assigned region at all. They fall
   * through to everything, which is worth a line of explanation on the page.
   */
  noRegions: boolean;
}

/**
 * The viewer's own regions, for pages that open on them when the address names
 * none. "Own" means ASSIGNED regions, never read breadth: a Regional Director
 * holds breadth_read:all, so breadth would make every RD's default national.
 */
export function useOwnRegions(): OwnRegions {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const { currentUser, loading: userLoading } = useCurrentUser();
  const { data, isLoading: hierarchyLoading } = useRegionHierarchy();

  const regionIds = projectRegions(currentUser).regionIds;
  const known = !permissionsLoading && !userLoading;
  const lacksNational = known && !hasPermission(Permission.READ_ALL);
  const scoped = lacksNational && regionIds.length > 0;
  const regions = data ? flattenRegions(data) : [];

  return {
    pending: !known || (scoped && hierarchyLoading),
    own: scoped ? shallowestFirst(regions.filter((r) => regionIds.includes(r.id))) : [],
    noRegions: lacksNational && regionIds.length === 0,
  };
}

/** By depth, keeping tree order among equals (the sort is stable). */
const shallowestFirst = (regions: ScopeRegion[]): ScopeRegion[] =>
  [...regions].sort((a, b) => a.depth - b.depth);
